import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { ArrowPathIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { getSteamAdminOverview, triggerSteamRefresh } from '../../api/steam';

const STATUS_STYLES = {
    success: 'bg-green-100 text-green-800',
    partial: 'bg-amber-100 text-amber-800',
    failed: 'bg-red-100 text-red-800',
    interrupted: 'bg-gray-200 text-gray-700',
    running: 'bg-blue-100 text-blue-800',
};

const StatusBadge = ({ status }) => (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-700'}`}>
        {status || 'none'}
    </span>
);

const getRelativeTime = (timestamp) => {
    if (!timestamp) return 'Never';

    const diffMs = Date.now() - new Date(timestamp).getTime();
    const future = diffMs < 0;
    const diffMins = Math.floor(Math.abs(diffMs) / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    let text;
    if (diffMins < 1) return future ? 'Any moment' : 'Just now';
    if (diffMins < 60) text = `${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
    else if (diffHours < 24) text = `${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
    else text = `${diffDays} day${diffDays !== 1 ? 's' : ''}`;

    return future ? `in ${text}` : `${text} ago`;
};

const formatDateTime = (timestamp) => (timestamp ? new Date(timestamp).toLocaleString('en-GB') : '—');

const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return '—';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
};

const formatNumber = (value) => (value === null || value === undefined ? '—' : value.toLocaleString('en-GB'));

const StatTile = ({ label, value, detail }) => (
    <div className="rounded-lg border border-gray-200 p-4">
        <p className="text-sm text-gray-500">{label}</p>
        <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
        {detail && <p className="mt-1 text-xs text-gray-500">{detail}</p>}
    </div>
);

export const SteamAdminPanel = ({ token }) => {
    const queryClient = useQueryClient();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [triggering, setTriggering] = useState(false);
    // Set to { runId } of the latest run when a manual refresh is queued; cleared
    // once the server reports it running or a newer run appears in the log
    const [queuedAfter, setQueuedAfter] = useState(null);
    const [message, setMessage] = useState(null);
    const queued = queuedAfter !== null;

    const { data, isLoading, isError, refetch } = useQuery(
        ['steamAdminOverview', token],
        () => getSteamAdminOverview(token).then((response) => response.data),
        {
            // Poll while a refresh is queued or running so the page updates when it finishes
            refetchInterval: (latest) => (queued || latest?.status?.is_running ? 5000 : false),
            onSuccess: (latest) => {
                if (
                    queuedAfter &&
                    (latest?.status?.is_running || latest?.status?.last_run?.refresh_id !== queuedAfter.runId)
                ) {
                    setQueuedAfter(null);
                }
            },
        }
    );

    const status = data?.status;
    const stats = data?.stats;
    const library = data?.library;
    const history = data?.history || [];
    const isRunning = Boolean(status?.is_running);

    const handleConfirmRefresh = async () => {
        setTriggering(true);
        setMessage(null);
        try {
            await triggerSteamRefresh(token);
            setQueuedAfter({ runId: status?.last_run?.refresh_id ?? null });
            setMessage({ type: 'success', text: 'Refresh started. This page updates when it finishes.' });
            queryClient.invalidateQueries('steamRefreshStatus');
            refetch();
        } catch (error) {
            setMessage({
                type: 'error',
                text: error.response?.data?.message || 'Failed to start the refresh. Please try again.',
            });
        } finally {
            setTriggering(false);
            setConfirmOpen(false);
        }
    };

    if (isLoading) {
        return <div className="py-12 text-center text-gray-600">Loading Steam refresh status...</div>;
    }

    if (isError) {
        return (
            <div className="rounded-lg border border-gray-200 bg-white p-6 text-center">
                <p className="text-red-600">Could not load the Steam refresh overview.</p>
                <button
                    onClick={() => refetch()}
                    className="mt-4 min-h-[44px] rounded-md bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200"
                >
                    Try again
                </button>
            </div>
        );
    }

    const successRate = stats?.total_runs
        ? Math.round(((stats.success_runs + stats.partial_runs) / stats.total_runs) * 100)
        : null;
    const achievementRate = library?.achievements_total
        ? Math.round((library.achievements_earned / library.achievements_total) * 100)
        : null;

    return (
        <div className="space-y-8">
            {/* Refresh controls */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-lg">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">Steam Library Refresh</h2>
                        <p className="mt-1 text-sm text-gray-500">
                            Runs daily at 05:00 UTC. Fetches owned games, player achievements and store details.
                        </p>
                        <Link
                            to="/steam/games"
                            className="mt-2 inline-flex min-h-[44px] items-center gap-1 text-sm font-semibold text-gray-700 underline decoration-gray-300 underline-offset-4 hover:text-gray-900"
                        >
                            View Steam library
                            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        </Link>
                    </div>
                    <button
                        onClick={() => setConfirmOpen(true)}
                        disabled={isRunning || queued || !data?.configured}
                        className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <ArrowPathIcon className={`h-5 w-5 ${isRunning || queued ? 'animate-spin' : ''}`} />
                        {isRunning ? 'Refreshing...' : queued ? 'Starting...' : 'Refresh now'}
                    </button>
                </div>

                {!data?.configured && (
                    <p className="mt-4 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
                        Steam refresh is not configured on the server (STEAM_WEB_API_KEY / STEAM_ID missing).
                    </p>
                )}

                {isRunning && (
                    <p className="mt-4 rounded-md bg-blue-50 p-3 text-sm text-blue-800">
                        Refresh in progress, started {getRelativeTime(status?.last_run?.started_at)}. A full run usually takes a few minutes.
                    </p>
                )}

                {message && (
                    <p className={`mt-4 rounded-md p-3 text-sm ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                        {message.text}
                    </p>
                )}

                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <StatTile
                        label="Last refreshed"
                        value={getRelativeTime(status?.last_refreshed_at)}
                        detail={formatDateTime(status?.last_refreshed_at)}
                    />
                    <div className="rounded-lg border border-gray-200 p-4">
                        <p className="text-sm text-gray-500">Last run</p>
                        <div className="mt-2">
                            <StatusBadge status={status?.last_run?.status} />
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                            {status?.last_run
                                ? `${status.last_run.triggered_by} · ${formatDuration(status.last_run.duration_seconds)}`
                                : 'No runs logged yet'}
                        </p>
                    </div>
                    <StatTile
                        label="Next scheduled run"
                        value={status?.next_run_at ? getRelativeTime(status.next_run_at) : 'Not scheduled'}
                        detail={status?.next_run_at ? formatDateTime(status.next_run_at) : null}
                    />
                    <StatTile
                        label={`Success rate (${stats?.days ?? 30} days)`}
                        value={successRate === null ? '—' : `${successRate}%`}
                        detail={stats?.total_runs
                            ? `${stats.success_runs} ok · ${stats.partial_runs} partial · ${stats.failed_runs} failed`
                            : 'No completed runs'}
                    />
                </div>
            </div>

            {/* Library topline numbers */}
            {library && (
                <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-lg">
                    <h2 className="mb-4 text-xl font-semibold text-gray-900">Library</h2>
                    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                        <StatTile
                            label="Games owned"
                            value={formatNumber(library.total_games)}
                            detail={`${formatNumber(library.games_played)} played`}
                        />
                        <StatTile
                            label="Total playtime"
                            value={`${formatNumber(Math.round(library.total_playtime_minutes / 60))}h`}
                            detail={`${formatNumber(library.games_played_last_2_weeks)} played in last 2 weeks`}
                        />
                        <StatTile
                            label="Achievements"
                            value={formatNumber(library.achievements_earned)}
                            detail={achievementRate === null
                                ? 'None tracked'
                                : `of ${formatNumber(library.achievements_total)} (${achievementRate}%) across ${formatNumber(library.games_with_achievements)} games`}
                        />
                        <StatTile
                            label="Data gaps"
                            value={formatNumber(library.games_missing_details + library.games_needing_review)}
                            detail={`${formatNumber(library.games_missing_details)} missing store details · ${formatNumber(library.games_needing_review)} flagged for review`}
                        />
                    </div>
                </div>
            )}

            {/* Run history */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-lg">
                <h2 className="mb-4 text-xl font-semibold text-gray-900">Recent Runs</h2>
                {history.length === 0 ? (
                    <p className="text-sm text-gray-500">No refreshes logged yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left text-sm">
                            <thead>
                                <tr className="bg-gray-200 tracking-tight">
                                    <th className="whitespace-nowrap p-2">Started</th>
                                    <th className="p-2">Status</th>
                                    <th className="p-2">Trigger</th>
                                    <th className="p-2">Duration</th>
                                    <th className="p-2 text-right">Games</th>
                                    <th className="whitespace-nowrap p-2 text-right">Achievement games</th>
                                    <th className="whitespace-nowrap p-2 text-right">Store details</th>
                                    <th className="p-2 text-right">Flagged</th>
                                    <th className="p-2">Error</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {history.map((run) => (
                                    <tr key={run.refresh_id} className="hover:bg-gray-50">
                                        <td className="whitespace-nowrap p-2">{formatDateTime(run.started_at)}</td>
                                        <td className="p-2"><StatusBadge status={run.status} /></td>
                                        <td className="p-2">{run.triggered_by}</td>
                                        <td className="p-2">{formatDuration(run.duration_seconds)}</td>
                                        <td className="p-2 text-right">{formatNumber(run.games_owned)}</td>
                                        <td className="p-2 text-right">{formatNumber(run.games_achievements_refreshed)}</td>
                                        <td className="p-2 text-right">{formatNumber(run.games_details_refreshed)}</td>
                                        <td className="p-2 text-right">{formatNumber(run.games_flagged_for_review)}</td>
                                        <td className="max-w-xs truncate p-2 text-red-700" title={run.error_message || ''}>
                                            {run.error_message || ''}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Refresh confirmation modal */}
            {confirmOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="w-full max-w-md rounded-lg bg-white p-6">
                        <h3 className="mb-4 text-lg font-semibold">Refresh Steam library?</h3>
                        <p className="mb-2 text-gray-600">
                            This fetches owned games, achievements and store details from the Steam API.
                        </p>
                        <p className="mb-6 text-sm text-gray-500">
                            It runs in the background and usually takes a few minutes. Last refreshed {getRelativeTime(status?.last_refreshed_at).toLowerCase()}.
                        </p>
                        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                            <button
                                onClick={() => setConfirmOpen(false)}
                                disabled={triggering}
                                className="min-h-[44px] rounded-md px-4 py-2 text-gray-600 hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmRefresh}
                                disabled={triggering}
                                className="min-h-[44px] rounded-md bg-gray-900 px-4 py-2 font-semibold text-white hover:bg-gray-700 disabled:opacity-50"
                            >
                                {triggering ? 'Starting...' : 'Start refresh'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
